/**
 * Dashboard API Service
 *
 * Provides methods for fetching dashboard configurations and data from the backend.
 */

import api from './api';

/**
 * Get the full dashboard YAML configuration
 * @returns {Promise} Promise resolving to config data
 */
export const getDashboardConfig = async () => {
  try {
    const response = await api.get('/api/dashboard/config');
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard config:', error);
    throw error;
  }
};

/**
 * Get list of available dashboards (tabs)
 * @returns {Promise} Promise resolving to dashboard list
 */
export const getDashboardList = async () => {
  try {
    const response = await api.get('/api/dashboard/list');
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard list:', error);
    throw error;
  }
};

/**
 * Get a specific dashboard configuration by ID
 * @param {string} dashboardId - Dashboard ID
 * @returns {Promise} Promise resolving to dashboard config
 */
export const getDashboardById = async (dashboardId) => {
  try {
    const response = await api.get(`/api/dashboard/${dashboardId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching dashboard ${dashboardId}:`, error);
    throw error;
  }
};

/**
 * Get chart configuration by ID
 * @param {string} chartId - Chart ID
 * @returns {Promise} Promise resolving to chart config
 */
export const getChartConfig = async (chartId) => {
  try {
    const response = await api.get(`/api/dashboard/chart/${chartId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching chart config ${chartId}:`, error);
    throw error;
  }
};

/**
 * Get rendered chart data ready for visualization
 * @param {string} chartId - Chart ID
 * @returns {Promise} Promise resolving to chart data
 */
export const getChartData = async (chartId) => {
  try {
    const response = await api.get(`/api/dashboard/chart/${chartId}/data`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching chart data for ${chartId}:`, error);
    throw error;
  }
};

/**
 * Get data for multiple charts in a single batch request
 * @param {string[]} chartIds - Array of chart IDs
 * @param {string} dashboardId - Dashboard ID (optional, for loading customizations)
 * @returns {Promise} Promise resolving to batch chart data response
 */
export const getBatchChartData = async (chartIds, dashboardId = null) => {
  try {
    const response = await api.post('/api/dashboard/charts/batch', {
      chart_ids: chartIds,
      dashboard_id: dashboardId,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching batch chart data:', error);
    throw error;
  }
};

/**
 * Get configuration summary
 * @returns {Promise} Promise resolving to config summary
 */
export const getConfigSummary = async () => {
  try {
    const response = await api.get('/api/dashboard/summary');
    return response.data;
  } catch (error) {
    console.error('Error fetching config summary:', error);
    throw error;
  }
};

/**
 * Get dashboard with user customizations applied
 * @param {string} dashboardId - Dashboard ID
 * @param {string} userId - User ID (default: 'global')
 * @returns {Promise} Promise resolving to customized dashboard config
 */
export const getCustomizedDashboard = async (dashboardId, userId = 'global') => {
  try {
    const response = await api.get(`/api/cards/dashboard/${dashboardId}/customized`, {
      params: { user_id: userId }
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching customized dashboard ${dashboardId}:`, error);
    throw error;
  }
};

export default {
  getDashboardConfig,
  getDashboardList,
  getDashboardById,
  getChartConfig,
  getChartData,
  getConfigSummary,
  getCustomizedDashboard,
};
